import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { useLegalBids } from '../hooks/useLegalBids';
import { POSITION_ORDER } from '../utils/bidValidation';
import {
  searchNotes,
  type ChapterSearchIndex,
  type ChapterResultGroup,
} from '../utils/notesSearch';
import { loadSearchIndexes } from '../utils/notesSearchIndexCache';
import type { AuctionBid, Position } from '../types/problems';
import { BidInput } from '../components/problems/BidInput';
import {
  BiddingSequenceBuilder,
  type BiddingSequenceBuilderHandle,
} from '../components/notes-search/BiddingSequenceBuilder';
import { SearchResults } from '../components/notes-search/SearchResults';
import { UnavailableChaptersNotice } from '../components/notes-search/UnavailableChaptersNotice';
import type { ChapterSummary } from '../api/learnApi';
import '../styles/learn-chapter.css';

/**
 * Feature 022 — Search Precision Notes by bidding sequence. Builds a client-side index of every
 * chapter's hand-bearing examples (fetched via the gated content endpoint), lets the learner build
 * an arbitrary sequence with the shared bid-picker, and shows the matching examples grouped by
 * chapter.
 */
export function NotesSearchPage() {
  usePageTitle('Search Precision Notes — Hawkes Bridge');
  const navigate = useNavigate();

  const [indexes, setIndexes] = useState<ChapterSearchIndex[] | null>(null);
  const [unavailable, setUnavailable] = useState<ChapterSummary[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [boxes, setBoxes] = useState<string[]>(['']);
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [groups, setGroups] = useState<ChapterResultGroup[] | null>(null);
  const builderRef = useRef<BiddingSequenceBuilderHandle>(null);
  const bidInputRef = useRef<HTMLDivElement>(null);

  // Boxes are not text-editable; tapping one moves focus to the bid picker (FR-017). Focus the first
  // enabled control if there is one, otherwise the container itself (which is focusable), so the
  // cursor lands on the bid input even when no bid can currently be entered.
  //
  // The container carries the highlight rather than the button: a programmatic focus() that follows a
  // mouse click does not satisfy :focus-visible, so a ring on the button alone would never paint and
  // the move would look like nothing happened. `focus-within` on the container fires either way.
  const focusBidInput = useCallback(() => {
    const container = bidInputRef.current;
    if (!container) return;
    const firstControl = container.querySelector('button:not([disabled])') as HTMLElement | null;
    (firstControl ?? container).focus({ preventScroll: true });
    container.scrollIntoView({ block: 'nearest' });
  }, []);

  // The index is fetched + parsed once per session and cached, so returning here from a result (the
  // common search → read → refine loop) is instant rather than re-parsing the whole book. A chapter
  // that could not be read comes back in `unavailable` and is disclosed rather than failing the page;
  // only a total failure rejects (FR-044).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { indexes: built, unavailable: missing } = await loadSearchIndexes();
        if (!active) return;
        setIndexes(built);
        setUnavailable(missing);
      } catch {
        if (active) setLoadFailed(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Legal bids for the picker are computed from the calls before the first empty box (FR-019).
  const targetIndex = boxes.indexOf('');
  const { auction, userPosition } = useMemo(() => {
    const prefix = targetIndex === -1 ? boxes : boxes.slice(0, targetIndex);
    const seq: AuctionBid[] = prefix.map((call, i) => ({
      seat: POSITION_ORDER[i % 4] as Position,
      call,
    }));
    return { auction: seq, userPosition: POSITION_ORDER[prefix.length % 4] as Position };
  }, [boxes, targetIndex]);

  const legalBids = useLegalBids({ auction, userPosition, selectedLevel });

  const handleBid = useCallback((call: string) => {
    builderRef.current?.addBid(call);
    setSelectedLevel(null);
  }, []);

  const handleSearch = useCallback(() => {
    const allFilled = builderRef.current?.validate() ?? false;
    if (!allFilled || !indexes) return; // empty boxes are now red-glowed; do not search
    setGroups(searchNotes(indexes, boxes));
  }, [boxes, indexes]);

  const handleClear = useCallback(() => {
    builderRef.current?.reset();
    setSelectedLevel(null);
    setGroups(null);
  }, []);

  // Same destination as the chapter reader's Back (FR-034): the Study page, which lists the
  // Precision notes chapters alongside the Search entry point that leads here. An explicit route
  // rather than history.back(), which would land wherever the learner happened to come from.
  //
  // This used to be `/dashboard?tab=learn&section=precision-notes`. Feature 023 re-homed that tab
  // to `/learn`, leaving Back pointing at a tab that no longer exists.
  const handleBack = useCallback(() => {
    navigate('/learn');
  }, [navigate]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={handleBack}
          aria-label="Back to Learn"
          className="inline-flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] px-3 py-2 rounded-md bg-cream hover:bg-cream border border-line text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
          <span className="hidden sm:inline">Back</span>
        </button>
        <h1 className="text-2xl font-bold text-ink">Search notes</h1>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <BiddingSequenceBuilder
            ref={builderRef}
            onSequenceChange={setBoxes}
            onBoxActivate={focusBidInput}
          />
        </div>
        <div className="flex gap-2 sm:pt-6">
          {/* Disabled until the index can answer (FR-043): a real `disabled` makes the press
              impossible, where aria-disabled would keep it pressable and preserve the silent no-op
              this exists to remove. The "Loading notes…" below is the explanation. */}
          <button
            type="button"
            onClick={handleSearch}
            disabled={indexes === null && !loadFailed}
            className="min-h-[44px] px-4 rounded-md bg-primary-600 text-white font-medium hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="min-h-[44px] px-4 rounded-md bg-cream text-ink font-medium hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            Clear
          </button>
        </div>
      </div>

      <div
        ref={bidInputRef}
        tabIndex={-1}
        data-testid="bid-input-container"
        className="mb-6 w-fit bg-card border border-line rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus-within:ring-2 focus-within:ring-primary-500"
      >
        <BidInput
          onSubmit={handleBid}
          disabled={targetIndex === -1}
          legalBids={legalBids}
          onLevelChange={setSelectedLevel}
          largerTargets
        />
      </div>

      {/* Above the results container, not inside it: the container scrolls internally (FR-028), so a
          notice placed within would scroll out of view exactly when a long result list makes it most
          relevant (FR-044). */}
      <UnavailableChaptersNotice chapters={unavailable} />

      <h2 className="text-lg font-semibold text-ink mb-2">Search results</h2>
      <div className="border border-line rounded-lg p-4 max-h-[calc(100vh_-_32rem)] min-h-[12rem] overflow-y-auto">
        {loadFailed ? (
          <p className="text-ink-soft">Unable to load notes content right now.</p>
        ) : indexes === null ? (
          <p role="status" className="text-ink-soft">
            Loading notes…
          </p>
        ) : (
          <SearchResults groups={groups} />
        )}
      </div>
    </div>
  );
}
